import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App } from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { listNotifications } from '../../services/notifications';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

  const NotificationsList = () => {
  const navigate = useNavigate();
  const { message } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items, total } = await listNotifications({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total,
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de notificaciones.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'view':
        navigate(`/notifications/view/${record.id}`);
        break;
      case 'edit':
        navigate(`/notifications/${record.id}/edit`);
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      can('notifications.view') && {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver Notificación',
      },
      can('notifications.edit') && {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar ntoficación',
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { title: 'Notificación', dataIndex: 'notification', key: 'notification', sorter: true, render: (notification) => notification?.description || '-', },
    { title: 'Módulo', dataIndex: 'module', key: 'module', sorter: true, render: (module) => module?.description || '-', },
    { title: 'Rol', dataIndex: 'notificationDetailRoles', key: 'notificationDetailRoles', sorter: false,
      render: (roles: { role: { name: string; description?: string } }[] | undefined) => {
        if (!roles || roles.length === 0) return '-';
        return roles
          .map((r) => r.role.description || r.role.name)
          .join(', ');
      },
    },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: true },
    { title: 'Incluir correos como copia oculta', dataIndex: 'hidden_copy', key: 'hidden_copy', sorter: true,
      render: (hiddenCopy) => (
          <Tag color={hiddenCopy ? 'green' : 'volcano'}>
            {hiddenCopy ? 'Sí' : 'No'}
          </Tag>
        ),
     },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>
          Notificaciones
        </Title>
      </div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Notificación"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} notificaciones`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default NotificationsList;
